import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/services/api.base.service';
import { environment } from '@env/environment.local';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CategoryType, DepartmentType } from './download-enums';

// Interfaces
export interface DownloadResponse {
  id: number;
  originalName: string;
  description?: string;
  departmentType?: DepartmentType;
  categoryType?: CategoryType;
  mimeType: string;
  urlSource: string;
  fileSize: number;
  creationDate: Date;
}

export interface DownloadListResponse {
  status: boolean;
  totalPages: number;
  page: number;
  totalElements: number;
  pageSize: number;
  data: DownloadResponse[];
}

export interface DownloadFilters {
  page?: number;
  pageSize?: number;
  departmentType?: DepartmentType;
  categoryType?: CategoryType;
  originalName?: string;
  description?: string;
  startDate?: Date;
  endDate?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class DownloadsService {
  constructor(public api: ApiService) { }

  getAllDownloads(params?: {
    page?: number;
    pageSize?: number;
    departmentType?: string;
    categoryType?: string;
    originalName?: string;
    description?: string;
    startDate?: Date;
    endDate?: Date;
  }): Observable<DownloadListResponse> {
    let httpParams = new HttpParams();
    
    if (params) {
      if (params.page) httpParams = httpParams.set('page', (params.page - 1).toString()); 
      if (params.pageSize) httpParams = httpParams.set('pagesize', params.pageSize.toString());
      
      if (params.departmentType) httpParams = httpParams.set('departmentType', params.departmentType);
      if (params.categoryType) httpParams = httpParams.set('categoryType', params.categoryType);
      if (params.originalName) httpParams = httpParams.set('originalName', params.originalName);
      if (params.description) httpParams = httpParams.set('description', params.description);
      if (params.startDate) httpParams = httpParams.set('startDate', params.startDate.toISOString().split('T')[0]);
      if (params.endDate) httpParams = httpParams.set('endDate', params.endDate.toISOString().split('T')[0]);
    }

    return this.api.getWithOptions<DownloadListResponse>(`${environment.API_FILE_SERVICE}/file/downloads`, { params: httpParams });
  }

  getDownloadById(downloadId: number): Observable<DownloadResponse> {
    return this.api.get(`${environment.API_FILE_SERVICE}/file/downloads/${downloadId}`);
  }

  registerDownloadAttempt(downloadId: number): Observable<void> {
    return this.api.put(`${environment.API_FILE_SERVICE}/file/downloads/${downloadId}/register`, {});
  }

  /**
   * URL pública temporal del blob (file-service). Debe coincidir con TypeFile.DOWNLOADS en el backend.
   */
  getDownloadPublicUrl(downloadId: number): Observable<string> {
    const params = new HttpParams().set('fileType', 'DOWNLOADS');
    return this.api
      .getWithOptions<{ status?: boolean; data?: { url?: string } }>(
        `${environment.API_FILE_SERVICE}/file/elements/${downloadId}/public-url`,
        { params }
      )
      .pipe(
        map((res) => {
          const url = res?.data?.url;
          if (!url) {
            throw new Error('URL pública no disponible');
          }
          return url;
        })
      );
  }
}
